import { getApiBaseUrl } from '../../../../../core/config/runtimeConfig';
import { useAuthStore } from '../../../../../core/store/auth.store';
import {
  CandidateSummaryStatusResponse,
  CandidateSummarySseStatusEvent,
} from '../models/candidate-summary.model';

export type CandidateSummarySseHandlers = {
  onStatus?: (event: CandidateSummarySseStatusEvent) => void;
  onCompleted: (response: CandidateSummaryStatusResponse) => void;
  onFailed: (response: CandidateSummaryStatusResponse) => void;
};

/** Parses one SSE block (`event:` / `data:` lines). Exported for unit testing. */
export function parseSseEventBlock(block: string): {
  eventName: string;
  data: string;
} | null {
  const trimmed = block.trim();
  if (!trimmed) {
    return null;
  }

  let eventName = 'message';
  const dataLines: string[] = [];

  for (const line of trimmed.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return { eventName, data: dataLines.join('\n') };
}

/**
 * Subscribes to candidate-summary job events using fetch (supports Bearer auth).
 * Resolves when the stream closes after a terminal event, or rejects on HTTP/network errors.
 */
export async function subscribeCandidateSummaryJobEvents(
  asyncJobId: string,
  handlers: CandidateSummarySseHandlers,
  signal?: AbortSignal
): Promise<void> {
  const auth = useAuthStore.getState().getAuth();
  const token = auth?.accessToken.accessToken;
  if (!token) {
    throw new Error('UNAUTHORIZED');
  }

  const response = await fetch(
    `${getApiBaseUrl()}/candidate-summary-jobs/${asyncJobId}/events`,
    {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      signal,
    }
  );

  if (response.status === 401) {
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok || !response.body) {
    throw new Error(`SSE connection failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminal = false;

  const dispatchBlock = (block: string) => {
    const parsedBlock = parseSseEventBlock(block);
    if (!parsedBlock) {
      return;
    }

    const payload = JSON.parse(parsedBlock.data) as unknown;

    if (parsedBlock.eventName === 'status') {
      handlers.onStatus?.(payload as CandidateSummarySseStatusEvent);
      return;
    }
    if (parsedBlock.eventName === 'completed') {
      terminal = true;
      handlers.onCompleted(payload as CandidateSummaryStatusResponse);
      return;
    }
    if (parsedBlock.eventName === 'failed') {
      terminal = true;
      handlers.onFailed(payload as CandidateSummaryStatusResponse);
    }
  };

  try {
    while (true) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatchBlock(block);
        boundary = buffer.indexOf('\n\n');
      }
    }

    const trailing = buffer.trim();
    if (trailing) {
      dispatchBlock(trailing);
    }
  } finally {
    reader.releaseLock();
  }

  if (!terminal) {
    throw new Error('SSE stream closed before job completed.');
  }
}
